// src/pages/Reports.js — Admin Portal report generation
import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { get } from '../utils/api';
import { Spinner, PageHeader, formatCurrency } from '../components/Shared';
import { MapContainer, TileLayer, Marker, Polyline, Popup, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { defaultIcon } from '../utils/leafletIcons';

// (stops-v1) Where the boy stopped on a round, classed against the saved shop locations.
const STOP_STYLE = {
  round:     { color: '#16a34a', label: 'Shop in this round' },
  other:     { color: '#2563eb', label: 'Other known shop' },
  unknown:   { color: '#dc2626', label: 'No known shop' },
  warehouse: { color: '#6b7280', label: 'Warehouse' },
};
const stopStyle = (kind) => STOP_STYLE[kind] || STOP_STYLE.unknown;
const stopTime = (iso) => new Date(iso).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false });
const stopMinutes = (sec) => (sec < 60 ? sec + ' sec' : (Math.round(sec / 6) / 10) + ' min');
const stopName = (s) => s.party_name || (s.kind === 'warehouse' ? 'Warehouse' : 'No known shop');

function StopsList({ stops }) {
  if (!stops || stops.length === 0) return null;
  const counts = {};
  stops.forEach(s => { counts[s.kind] = (counts[s.kind] || 0) + 1; });
  return (
    <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 max-h-44 overflow-auto px-5 py-2 text-xs">
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2 text-gray-500 dark:text-gray-400">
        {Object.keys(STOP_STYLE).map(k => (
          <span key={k} className="inline-flex items-center gap-1">
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: STOP_STYLE[k].color, display: 'inline-block' }} />
            {STOP_STYLE[k].label}{counts[k] ? ' (' + counts[k] + ')' : ''}
          </span>
        ))}
      </div>
      {stops.map((s, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5 text-gray-700 dark:text-gray-200">
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: stopStyle(s.kind).color, flexShrink: 0 }} />
          <span className="w-24 shrink-0 text-gray-400">{stopTime(s.started_at)}–{stopTime(s.ended_at)}</span>
          <span className="w-16 shrink-0">{stopMinutes(s.seconds)}</span>
          <span className="truncate">{stopName(s)}</span>
        </div>
      ))}
    </div>
  );
}

// Small modal showing a completed round's actual GPS trail as a path on
// a map. Fetches on open, closes cleanly, no external state needed.
function RoundRouteMap({ roundId, roundLabel, onClose }) {
  const [trail, setTrail] = useState(null);
  const [matchedSegments, setMatchedSegments] = useState([]);
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    get(`/api/dispatch/rounds/${roundId}/trail`)
      .then(res => {
        if (!cancelled) {
          setTrail(res.trail || []);
          setMatchedSegments(res.matched_segments || []);
          setStops(res.stops || []);
          setLoading(false);
        }
      })
      .catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [roundId]);

  const rawPositions = (trail || []).map(p => [parseFloat(p.lat), parseFloat(p.lng)]);
  // Road-snapped segments are the preferred display — they follow actual
  // streets instead of cutting straight lines across whatever's between
  // two GPS pings. Falls back to the raw points if OSRM couldn't match
  // this particular trace (e.g. too few points, or GPS was too sparse).
  const hasMatchedRoute = matchedSegments.length > 0;
  const center = rawPositions.length
    ? rawPositions[Math.floor(rawPositions.length / 2)]
    : [12.3052, 76.6552]; // Mysuru fallback

  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div>
            <p className="font-bold text-gray-800 dark:text-gray-100">{roundLabel}</p>
            {!loading && !error && rawPositions.length > 0 && (
              <p className="text-xs text-gray-400">
                {hasMatchedRoute ? '📍 Road-matched route' : '📍 Direct GPS trail'}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="flex-1 relative">
          {loading && <div className="absolute inset-0 flex items-center justify-center text-gray-400">Loading route…</div>}
          {error && <div className="absolute inset-0 flex items-center justify-center text-red-500 text-sm px-6 text-center">{error}</div>}
          {!loading && !error && rawPositions.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm px-6 text-center">
              No GPS points were recorded for this round.
            </div>
          )}
          {!loading && !error && rawPositions.length > 0 && (
            <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {hasMatchedRoute ? (
                matchedSegments.map((segment, i) => (
                  <Polyline key={i} positions={segment} pathOptions={{ color: '#e2711d', weight: 4 }} />
                ))
              ) : (
                <Polyline positions={rawPositions} pathOptions={{ color: '#e2711d', weight: 4 }} />
              )}
              <Marker position={rawPositions[0]} icon={defaultIcon}><Popup>Round started here</Popup></Marker>
              {rawPositions.length > 1 && (
                <Marker position={rawPositions[rawPositions.length - 1]} icon={defaultIcon}><Popup>Most recent point</Popup></Marker>
              )}
              {stops.map((s, i) => (
                <CircleMarker key={'stop' + i} center={[s.lat, s.lng]} radius={9}
                  pathOptions={{ color: '#ffffff', weight: 2, fillColor: stopStyle(s.kind).color, fillOpacity: 0.95 }}>
                  <Popup>
                    <b>{stopName(s)}</b><br />
                    {stopMinutes(s.seconds)} · {stopTime(s.started_at)}–{stopTime(s.ended_at)}
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          )}
        </div>
        <StopsList stops={stops} />
      </div>
    </div>
  );
}

function localDateString(d) {
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().split('T')[0];
}
const today   = () => localDateString(new Date());
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return localDateString(d); };

// NOTE: this is the same protected invoice-stage color-coding system as
// STATUS_COLORS in Shared.js, just implemented with hex values here
// instead of Tailwind classes. Left completely untouched.
const STAGE_COLORS = {
  stage_1_erp:           { bg: '#e5e7eb', text: '#374151', label: 'ERP Received' },
  stage_2_stock_removal: { bg: '#fef3c7', text: '#92400e', label: 'Stock Removal' },
  stage_3_checking:      { bg: '#dbeafe', text: '#1d4ed8', label: 'Checking' },
  stage_4_packing:       { bg: '#f3e8ff', text: '#7e22ce', label: 'Ready for Delivery' },
  stage_5_dispatched:    { bg: '#e0e7ff', text: '#4338ca', label: 'In Transit' },
  stage_6_delivered:     { bg: '#dcfce7', text: '#166534', label: 'Delivered' },
  reattempt_pending:     { bg: '#ffedd5', text: '#9a3412', label: 'Reattempt' },
  cancelled:             { bg: '#fee2e2', text: '#991b1b', label: 'Cancelled' },
  complete:              { bg: '#d1fae5', text: '#065f46', label: 'Complete' },
};
function stageStyle(status) {
  return STAGE_COLORS[status] || { bg: '#f3f4f6', text: '#4b5563', label: (status || '—').replace(/_/g, ' ') };
}

const REPORT_TYPES = [
  { value: 'date_wise',   label: '📅 Date Wise',   desc: 'Activity broken down day by day' },
  { value: 'party_wise',  label: '🏢 Party Wise',  desc: 'Invoices grouped by customer' },
  { value: 'staff_wise',  label: '👤 Staff Wise',  desc: 'Work done by individual team members' },
  { value: 'trip_report', label: '🚦 Round Report', desc: 'Delivery person round-by-round summaries' },
];

function formatDuration(seconds) {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', weekday: 'short' });
}

function buildCSVString(headers, rows) {
  return [
    headers.join(','),
    ...rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
  ].join('\n');
}
function previewCSV(headers, rows) {
  const csv = buildCSVString(headers, rows);
  const blob = new Blob([csv], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}

function downloadXLSX(headers, rows, filename, textColumns = []) {
  const wsData = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  textColumns.forEach(colIdx => {
    for (let r = 1; r < wsData.length; r++) {
      const cellRef = XLSX.utils.encode_cell({ r, c: colIdx });
      const cell = ws[cellRef];
      if (cell) cell.t = 's';
    }
  });

  const colWidths = headers.map((h, colIdx) => {
    let maxLen = String(h ?? '').length;
    rows.forEach(row => {
      const len = String(row[colIdx] ?? '').length;
      if (len > maxLen) maxLen = len;
    });
    return { wch: Math.min(maxLen + 2, 40) };
  });
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, filename);
}

function printReportA4(title, headers, rows) {
  const win = window.open('', '_blank');
  const rowsHtml = rows.map(r =>
    `<tr>${r.map(v => `<td>${String(v ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;')}</td>`).join('')}</tr>`
  ).join('');
  const headHtml = headers.map(h => `<th>${h}</th>`).join('');
  win.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        @page { size: A4; margin: 12mm; }
        * { box-sizing: border-box; }
        body { font-family: Arial, sans-serif; font-size: 10px; color: #111; margin: 0; }
        h1 { font-size: 15px; margin: 0 0 4px 0; }
        p.meta { font-size: 10px; color: #666; margin: 0 0 14px 0; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; word-break: break-word; }
        th { background: #f3f4f6; font-weight: 700; }
        tr { page-break-inside: avoid; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      </style>
    </head>
    <body>
      <h1>${title}</h1>
      <p class="meta">Generated ${new Date().toLocaleString('en-IN')} — Mangal Distributors</p>
      <table>
        <thead><tr>${headHtml}</tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <script>window.onload = () => { window.print(); };</script>
    </body>
    </html>
  `);
  win.document.close();
}

// Builds one combined WhatsApp message for a "parcel" — one or more
// outstation invoices for the same party that went out together, matching
// how the delivery app already groups same-party outstation invoices into
// a single card at handover time.
function buildWhatsAppLink(invGroup) {
  const first = invGroup[0];
  const phone = (first.party_phone || '').replace(/[^0-9]/g, '');
  if (!phone) return null;
  const fullPhone = phone.length === 10 ? `91${phone}` : phone;

  const totalArticles = invGroup.reduce((sum, i) => sum + (i.article_count || 0), 0);
  const invoiceNumbers = invGroup.map(i => i.invoice_number).join(', ');
  const totalValue = invGroup.reduce((sum, i) => sum + (parseFloat(i.invoice_value) || 0), 0);
  const transportName = invGroup.find(i => i.transport_name)?.transport_name || '';
  const notes = invGroup.find(i => i.handover_notes)?.handover_notes || '';

  const lines = [
    `Hello ${first.party_name},`,
    ``,
    `Your parcel of ${totalArticles || '—'} article${totalArticles !== 1 ? 's' : ''} for`,
    `Invoice${invGroup.length > 1 ? 's' : ''} ${invoiceNumbers} worth ₹${totalValue.toLocaleString('en-IN')}`,
    `has been dispatched through ${transportName || 'our transport partner'}.`,
  ];
  if (notes) lines.push(notes);
  lines.push(``, `Regards,`, `Mangal Distributors`);

  const message = lines.join('\n');
  return `https://wa.me/${fullPhone}?text=${encodeURIComponent(message)}`;
}

export default function Reports() {
  const [reportType, setReportType] = useState('date_wise');
  const [from,       setFrom]       = useState(today());
  const [to,         setTo]         = useState(today());
  const [users,      setUsers]      = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [generated,  setGenerated]  = useState(false);
  const [sortCol,    setSortCol]    = useState(null);
  const [sortOrder,  setSortOrder]  = useState('asc');
  const [partyNameFilter, setPartyNameFilter] = useState('');
  const [deliveryType, setDeliveryType] = useState(''); // '' | 'instation' | 'outstation'
  const [mapRound, setMapRound] = useState(null); // { roundId, label } | null — opens RoundRouteMap when set

  function handleSort(col) {
    if (col === sortCol) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortOrder('asc'); }
  }

  useEffect(() => {
    async function loadUsers() {
      try {
        const res = await get('/api/users?limit=200');
        setUsers(res.users || []);
      } catch (e) { console.error(e); }
    }
    loadUsers();
  }, []);

  function toggleUser(id) {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  const generate = useCallback(async () => {
    setLoading(true);
    setData(null);
    setGenerated(false);
    try {
      if (reportType === 'trip_report') {
        let url = `/api/deliveries/trips?from=${from}&to=${to}`;
        if (selectedUsers.length === 1) url += `&user_id=${selectedUsers[0]}`;
        const res = await get(url);
        setData(res.trips || []);
      } else {
        let url = `/api/invoices?limit=2000&offset=0&invoice_date_from=${from}&invoice_date_to=${to}`;
        if (deliveryType) url += `&delivery_type=${encodeURIComponent(deliveryType)}`;
        if (reportType === 'staff_wise' && selectedUsers.length > 0) {
          url += `&handled_by=${selectedUsers.join(',')}`;
        }
        const res = await get(url);
        setData(res.invoices || []);
      }
      setGenerated(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [reportType, from, to, selectedUsers, deliveryType]);

  function getDateWiseData() {
    const grouped = {};
    (data || []).forEach(inv => {
      const date = inv.received_at ? new Date(inv.received_at).toISOString().split('T')[0] : 'Unknown';
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(inv);
    });
    return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
  }

  function getPartyWiseData() {
    const grouped = {};
    (data || []).forEach(inv => {
      const key = inv.party_name || 'Unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(inv);
    });
    let entries = Object.entries(grouped);
    if (partyNameFilter.trim()) {
      const q = partyNameFilter.trim().toLowerCase();
      entries = entries.filter(([party]) => party.toLowerCase().includes(q));
    }
    return entries.sort((a, b) => b[1].length - a[1].length);
  }

  function buildReportData() {
    if (reportType === 'trip_report') {
      const headers = ['Round', 'Delivery Person', 'Phone', 'Invoice #', 'Party', 'City', 'Outcome', 'Voucher Status', 'Date', 'Round Start', 'Round End', 'Duration', 'Total Invoices', 'Delivered', 'Reattempts', 'Cancelled'];
      const rows = (data || []).flatMap(t =>
        (t.invoices && t.invoices.length ? t.invoices : [{}]).map(inv => [
          t.round_label || t.round_number, t.delivery_person_name, t.delivery_person_phone || '',
          inv.invoice_number || '', inv.party_name || '', inv.city || '',
          inv.outcome || '', (inv.voucher_phase === 'confirmed' && inv.confirmation_type) ? inv.confirmation_type.replace(/_/g, ' ') : '',
          String(t.trip_date).split('T')[0],
          formatDateTime(t.trip_start), t.trip_end ? formatDateTime(t.trip_end) : 'In progress',
          formatDuration(t.trip_duration_seconds),
          t.total_invoices, t.delivered, t.reattempts, t.cancelled,
        ])
      );
      return { headers, rows, textColumns: [2] };
    } else if (reportType === 'date_wise') {
      const headers = ['Date', 'Invoice #', 'Party', 'City', 'Stage', 'Value', 'Articles', 'Transport', 'Delivery Started', 'Duration', 'Outcome'];
      const isDelivered = (inv) => ['stage_6_delivered', 'complete'].includes(inv.status);
      const rows = getDateWiseData().flatMap(([date, invs]) =>
        invs.map(inv => [date, inv.invoice_number, inv.party_name, inv.city, stageStyle(inv.status).label, inv.invoice_value,
          isDelivered(inv) ? (inv.article_count ?? '') : '',
          isDelivered(inv) ? (inv.transport_name ?? '') : '',
          inv.delivery_started_at ? formatDateTime(inv.delivery_started_at) : '',
          inv.delivery_duration_seconds ? formatDuration(inv.delivery_duration_seconds) : '',
          inv.voucher_outcome ? inv.voucher_outcome.replace(/_/g, ' ') : ''])
      );
      return { headers, rows, textColumns: [] };
    } else if (reportType === 'party_wise') {
      const headers = ['Party', 'City', 'Invoice #', 'Date', 'Stage', 'Value', 'Articles', 'Transport', 'Delivery Started', 'Duration', 'Outcome'];
      const isDelivered2 = (inv) => ['stage_6_delivered', 'complete'].includes(inv.status);
      const rows = getPartyWiseData().flatMap(([party, invs]) =>
        invs.map(inv => [party, inv.city, inv.invoice_number, inv.received_at?.split('T')[0], stageStyle(inv.status).label, inv.invoice_value,
          isDelivered2(inv) ? (inv.article_count ?? '') : '',
          isDelivered2(inv) ? (inv.transport_name ?? '') : '',
          inv.delivery_started_at ? formatDateTime(inv.delivery_started_at) : '',
          inv.delivery_duration_seconds ? formatDuration(inv.delivery_duration_seconds) : '',
          inv.voucher_outcome ? inv.voucher_outcome.replace(/_/g, ' ') : ''])
      );
      return { headers, rows, textColumns: [] };
    } else {
      const headers = ['Invoice #', 'Party', 'City', 'Stage', 'Handler', 'Value', 'Date', 'Articles', 'Transport', 'Delivery Started', 'Duration', 'Outcome'];
      const isDelivered3 = (inv) => ['stage_6_delivered', 'complete'].includes(inv.status);
      const rows = (data || []).map(inv => [
        inv.invoice_number, inv.party_name, inv.city, stageStyle(inv.status).label,
        inv.current_handler_name, inv.invoice_value,
        inv.received_at?.split('T')[0],
        isDelivered3(inv) ? (inv.article_count ?? '') : '',
        isDelivered3(inv) ? (inv.transport_name ?? '') : '',
        inv.delivery_started_at ? formatDateTime(inv.delivery_started_at) : '',
        inv.delivery_duration_seconds ? formatDuration(inv.delivery_duration_seconds) : '',
        inv.voucher_outcome ? inv.voucher_outcome.replace(/_/g, ' ') : '',
      ]);
      return { headers, rows, textColumns: [] };
    }
  }

  function handleExport() {
    const filename = `DMS_${reportType}_${from}_to_${to}.xlsx`;
    const { headers, rows, textColumns } = buildReportData();
    downloadXLSX(headers, rows, filename, textColumns);
  }

  function handleViewCSV() {
    const { headers, rows } = buildReportData();
    previewCSV(headers, rows);
  }

  function handlePrint() {
    const { headers, rows } = buildReportData();
    const title = `DMS ${REPORT_TYPES.find(r => r.value === reportType)?.label || 'Report'} — ${from} to ${to}`;
    printReportA4(title, headers, rows);
  }

  // NOTE: role-color-coding left untouched — meaningful, established
  // color-code for distinguishing team roles at a glance, same category
  // as the invoice stage colors.
  const staffRoleColors = {
    owner: 'bg-red-100 text-red-700',
    team_leader: 'bg-purple-100 text-purple-700',
    stock_removal: 'bg-yellow-100 text-yellow-700',
    checking: 'bg-blue-100 text-blue-700',
    packing: 'bg-indigo-100 text-indigo-700',
    delivery_person: 'bg-green-100 text-green-700',
  };

  return (
    <div className="p-6 max-w-[1600px]">
      <div className="flex items-baseline gap-3 flex-wrap mb-4">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Reports</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Generate date-wise, party-wise, staff-wise and trip reports</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
        <div className="mb-5">
          <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">Report Type</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {REPORT_TYPES.map(rt => (
              <button key={rt.value} onClick={() => { setReportType(rt.value); setData(null); setGenerated(false); }}
                className={`p-3 rounded-xl border-2 text-left transition-all ${reportType === rt.value ? 'border-brand-700 bg-brand-700/5' : 'border-gray-200 dark:border-gray-700 hover:border-brand-500'}`}>
                <p className="text-sm font-bold text-gray-800 dark:text-gray-100">{rt.label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{rt.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mb-5">
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">From Date</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">To Date</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div className="flex items-end gap-2">
            {[['Today', today(), today()], ['Last 7', daysAgo(7), today()], ['Last 30', daysAgo(30), today()], ['This Month', localDateString(new Date()).slice(0,8)+'01', today()]].map(([label, f, t]) => (
              <button key={label} onClick={() => { setFrom(f); setTo(t); }}
                className="px-3 py-2 text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200">
                {label}
              </button>
            ))}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Type</label>
            <div className="flex gap-1">
              {[['','All'],['instation','🏙️ Instation'],['outstation','🚛 Outstation']].map(([val,lbl]) => (
                <button key={val} onClick={() => setDeliveryType(val)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors
                    ${deliveryType === val
                      ? 'bg-brand-700 text-white border-brand-700'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-brand-500'}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-end">
            <button onClick={generate} disabled={loading}
              className="px-6 py-2 bg-brand-700 text-white rounded-xl font-bold text-sm hover:bg-brand-500 disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating...
                </span>
              ) : '📊 Generate Report'}
            </button>
          </div>
        </div>

        {reportType === 'party_wise' && (
          <div className="mb-5">
            <label className="block text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Filter by Party Name <span className="text-xs font-normal text-gray-400 dark:text-gray-500">(leave empty to show all)</span>
            </label>
            <input type="text" value={partyNameFilter} onChange={e => setPartyNameFilter(e.target.value)}
              placeholder="e.g. Jeevan Medical"
              className="w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
        )}

        {(reportType === 'staff_wise' || reportType === 'trip_report') && (
          <div className="mb-5">
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Filter by Team Member <span className="text-xs font-normal text-gray-400 dark:text-gray-500">(leave empty to show all)</span>
            </p>
            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
              {users.filter(u => reportType === 'trip_report' ? u.role === 'delivery_person' : true).map(u => (
                <button key={u.id} onClick={() => toggleUser(u.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${selectedUsers.includes(u.id) ? 'border-brand-700 bg-brand-700 text-white' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-brand-500'}`}>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${staffRoleColors[u.role] || 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}>{u.role?.replace(/_/g, ' ')}</span>
                  {u.name}
                </button>
              ))}
            </div>
            {selectedUsers.length > 0 && (
              <button onClick={() => setSelectedUsers([])} className="mt-2 text-xs text-brand-500 hover:underline">
                Clear selection ({selectedUsers.length} selected)
              </button>
            )}
          </div>
        )}

      </div>

      {loading && <div className="flex justify-center py-16"><Spinner size="lg" /></div>}

      {generated && !loading && data !== null && (
        <div>
          <div className="flex justify-between items-center mb-4 sticky top-0 z-20 bg-gray-100 dark:bg-gray-900 py-2">
            <div>
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">{REPORT_TYPES.find(r => r.value === reportType)?.label} Report</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatDate(from)} → {formatDate(to)}
                {selectedUsers.length > 0 && ` · ${selectedUsers.length} member(s) selected`}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={handleViewCSV}
                className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 font-semibold flex items-center gap-2">
                👁 View CSV
              </button>
              <button onClick={handlePrint}
                className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 font-semibold flex items-center gap-2">
                🖨 Print (A4)
              </button>
              <button onClick={handleExport}
                className="px-4 py-2 bg-green-700 text-white text-sm rounded-lg hover:bg-green-600 font-semibold flex items-center gap-2">
                ⬇ Export XLSX
              </button>
            </div>
          </div>

          {reportType === 'trip_report' && (
            data.length === 0 ? <EmptyState /> : (
              <div className="space-y-4">
                {data.map((trip, i) => (
                  <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-5 py-4 bg-brand-700/5 border-b border-brand-700/10 flex flex-wrap justify-between items-start gap-3">
                      <div>
                        <p className="font-bold text-brand-700 text-lg">{trip.delivery_person_name} — Round {trip.round_label || `#${trip.round_number}`}</p>
                        <p className="text-sm text-brand-500">{trip.delivery_person_phone} · {formatDate(trip.trip_date)}</p>
                      </div>
                      <div className="flex gap-3 flex-wrap items-start">
                        <Stat label="Duration" value={formatDuration(trip.trip_duration_seconds)} color="blue" />
                        <Stat label="Invoices" value={trip.total_invoices} color="gray" />
                        <Stat label="Delivered" value={trip.delivered} color="green" />
                        <Stat label="Reattempt" value={trip.reattempts} color="amber" />
                        <Stat label="Cancelled" value={trip.cancelled} color="red" />
                        <button
                          onClick={() => setMapRound({ roundId: trip.round_id, label: `${trip.delivery_person_name} — Round ${trip.round_label || `#${trip.round_number}`}` })}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-accent-500 text-white hover:bg-accent-500/90 self-center">
                          🗺 View Route
                        </button>
                      </div>
                    </div>
                    <div className="px-5 py-3 bg-gray-50 dark:bg-gray-900 border-b flex gap-8 text-sm">
                      <div>
                        <span className="text-gray-400 dark:text-gray-500 text-xs">Round Start</span>
                        <p className="font-semibold text-gray-700 dark:text-gray-300">{formatDateTime(trip.trip_start)}</p>
                      </div>
                      <div>
                        <span className="text-gray-400 dark:text-gray-500 text-xs">Round End</span>
                        <p className="font-semibold text-gray-700 dark:text-gray-300">{trip.trip_end ? formatDateTime(trip.trip_end) : 'Still in progress'}</p>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {(trip.invoices || []).map((inv, j) => (
                        <div key={j} className="px-5 py-2.5 flex justify-between items-center text-sm">
                          <div>
                            <span className="font-mono font-semibold text-gray-800 dark:text-gray-100">{inv.invoice_number}</span>
                            <span className="text-gray-400 dark:text-gray-500 mx-2">·</span>
                            <span className="text-gray-600 dark:text-gray-400">{inv.party_name}</span>
                            <span className="text-gray-400 dark:text-gray-500 text-xs ml-2">{inv.city}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              inv.outcome === 'signed' || inv.outcome === 'neighbour' ? 'bg-green-100 text-green-700' :
                              inv.outcome === 'reattempt' ? 'bg-amber-100 text-amber-700' :
                              inv.outcome === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                            }`}>
                              {inv.outcome}
                            </span>
                            {inv.voucher_phase === 'confirmed' && inv.confirmation_type && (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                                {inv.confirmation_type.replace(/_/g, ' ')}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {reportType === 'date_wise' && (
            data.length === 0 ? <EmptyState /> : (
              <div className="space-y-4">
                {getDateWiseData().map(([date, invs]) => (
                  <div key={date} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 dark:bg-gray-900 border-b flex justify-between items-center">
                      <p className="font-bold text-gray-800 dark:text-gray-100">{formatDate(date)}</p>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{invs.length} invoice{invs.length !== 1 ? 's' : ''} · {formatCurrency(invs.reduce((s, i) => s + (parseFloat(i.invoice_value) || 0), 0))}</span>
                    </div>
                    <InvoiceTable invoices={invs} sortCol={sortCol} sortOrder={sortOrder} onSort={handleSort} />
                  </div>
                ))}
              </div>
            )
          )}

          {reportType === 'party_wise' && (
            data.length === 0 ? <EmptyState /> : (
              <div className="space-y-4">
                {getPartyWiseData().map(([party, invs]) => (
                  <div key={party} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 dark:bg-gray-900 border-b flex justify-between items-center">
                      <div>
                        <p className="font-bold text-gray-800 dark:text-gray-100">{party}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">{invs[0]?.city}</p>
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{invs.length} invoice{invs.length !== 1 ? 's' : ''} · {formatCurrency(invs.reduce((s, i) => s + (parseFloat(i.invoice_value) || 0), 0))}</span>
                    </div>
                    <InvoiceTable invoices={invs} sortCol={sortCol} sortOrder={sortOrder} onSort={handleSort} />
                  </div>
                ))}
              </div>
            )
          )}

          {reportType === 'staff_wise' && (
            data.length === 0 ? <EmptyState /> : (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <InvoiceTable invoices={data} showHandler sortCol={sortCol} sortOrder={sortOrder} onSort={handleSort} />
              </div>
            )
          )}
        </div>
      )}

      {mapRound && (
        <RoundRouteMap
          roundId={mapRound.roundId}
          roundLabel={mapRound.label}
          onClose={() => setMapRound(null)}
        />
      )}
    </div>
  );
}

function outcomeStyle(outcome) {
  if (!outcome) return null;
  if (['signed', 'neighbour'].includes(outcome)) return { bg: '#dcfce7', text: '#166534' };
  if (outcome === 'reattempt') return { bg: '#fef3c7', text: '#92400e' };
  if (['cancelled', 'returned'].includes(outcome)) return { bg: '#fee2e2', text: '#991b1b' };
  return { bg: '#f3f4f6', text: '#4b5563' };
}

function InvoiceTable({ invoices, showHandler, sortCol, sortOrder, onSort }) {
  const isDelivered = (inv) => ['stage_6_delivered', 'complete'].includes(inv.status);
  const COLUMNS = [
    { key: 'invoice_number', label: 'Invoice #' },
    { key: 'party_name',     label: 'Party' },
    { key: 'city',           label: 'City' },
    { key: 'status',         label: 'Stage' },
    showHandler ? { key: 'current_handler_name', label: 'Handler' } : null,
    { key: 'invoice_value',  label: 'Value' },
    { key: 'transport_name', label: 'Transport' },
    { key: 'received_at',    label: 'Date' },
    { key: 'delivery_started_at', label: 'Delivery Started' },
    { key: 'delivery_duration_seconds', label: 'Duration' },
    { key: 'voucher_outcome', label: 'Outcome' },
    { key: '_whatsapp',      label: '' },
  ].filter(Boolean);

  function sortIcon(key) {
    if (sortCol !== key) return <span style={{ color: '#d1d5db', marginLeft: '4px' }}>↕</span>;
    return <span style={{ color: '#e2711d', marginLeft: '4px' }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  }

  const sorted = [...invoices].sort((a, b) => {
    if (!sortCol) return 0;
    let av = a[sortCol], bv = b[sortCol];
    if (sortCol === 'invoice_value' || sortCol === 'delivery_duration_seconds') { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
    else if (sortCol === 'received_at' || sortCol === 'delivery_started_at') { av = av ? new Date(av).getTime() : 0; bv = bv ? new Date(bv).getTime() : 0; }
    else { av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase(); }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  // Group same-party outstation invoices into "parcels" — matches how
  // the delivery app already groups them into one card at handover time.
  const parcelGroups = {};
  invoices.forEach(inv => {
    if (inv.delivery_type !== 'outstation') return;
    const key = inv.party_id || `${inv.party_name}__${inv.city}`;
    if (!parcelGroups[key]) parcelGroups[key] = [];
    parcelGroups[key].push(inv);
  });
  const parcelRepresentative = {};
  Object.values(parcelGroups).forEach(group => {
    const rep = [...group].sort((a, b) => (a.invoice_number || '').localeCompare(b.invoice_number || ''))[0];
    parcelRepresentative[rep.invoice_id] = group;
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-900">
          <tr>
            {COLUMNS.map(c => (
              <th key={c.key} onClick={() => c.key !== '_whatsapp' && onSort(c.key)}
                className={`text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase select-none ${c.key !== '_whatsapp' ? 'cursor-pointer hover:bg-gray-100 dark:bg-gray-800' : ''}`}>
                {c.label}{c.key !== '_whatsapp' && sortIcon(c.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((inv, i) => {
            const st = stageStyle(inv.status);
            const parcelGroup = parcelRepresentative[inv.invoice_id];
            const waLink = parcelGroup ? buildWhatsAppLink(parcelGroup) : null;
            return (
              <tr key={i} className="border-t hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-4 py-2.5 font-mono font-semibold text-brand-700 text-xs">{inv.invoice_number}</td>
                <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-100 text-xs">{inv.party_name}</td>
                <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 text-xs">{inv.city}</td>
                <td className="px-4 py-2.5">
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: st.bg, color: st.text }}>{st.label}</span>
                </td>
                {showHandler && <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 text-xs">{inv.current_handler_name || '—'}</td>}
                <td className="px-4 py-2.5 font-medium text-xs">{formatCurrency(inv.invoice_value)}</td>
                <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 text-xs">{isDelivered(inv) ? (inv.transport_name || '—') : '—'}</td>
                <td className="px-4 py-2.5 text-gray-400 dark:text-gray-500 text-xs">{inv.received_at ? new Date(inv.received_at).toLocaleDateString('en-IN') : '—'}</td>
                <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 text-xs">{inv.delivery_started_at ? formatDateTime(inv.delivery_started_at) : '—'}</td>
                <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 text-xs">{formatDuration(inv.delivery_duration_seconds)}</td>
                <td className="px-4 py-2.5">
                  {inv.voucher_outcome ? (
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={outcomeStyle(inv.voucher_outcome)}>
                      {inv.voucher_outcome.replace(/_/g, ' ')}
                    </span>
                  ) : <span className="text-gray-300 text-xs">—</span>}
                </td>
                <td className="px-4 py-2.5">
                  {waLink && (
                    <button
                      onClick={() => window.open(waLink, 'dms_whatsapp_sender', 'width=420,height=680')}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-100"
                      title={parcelGroup.length > 1
                        ? `Send one combined update for all ${parcelGroup.length} invoices in this parcel: ${parcelGroup.map(x => x.invoice_number).join(', ')}`
                        : 'Send shipment update on WhatsApp'}>
                      💬 WhatsApp{parcelGroup.length > 1 ? ` (${parcelGroup.length})` : ''}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// NOTE: Stat's color options left untouched — semantic metric-type
// colors (blue=info, green=good, etc.), same reasoning as KpiCard.
function Stat({ label, value, color }) {
  const colors = { blue: 'bg-blue-100 text-blue-800', green: 'bg-green-100 text-green-800', amber: 'bg-amber-100 text-amber-800', red: 'bg-red-100 text-red-800', gray: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' };
  return (
    <div className={`px-3 py-1.5 rounded-lg text-center ${colors[color]}`}>
      <p className="text-lg font-bold">{value}</p>
      <p className="text-xs">{label}</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-16 text-center">
      <p className="text-4xl mb-3">📊</p>
      <p className="text-gray-500 dark:text-gray-400 font-medium">No data found for this period</p>
      <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Try adjusting the date range or filters</p>
    </div>
  );
}
